// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';
import {
  Box, Icon, IconName, MultiSelect, Text, Tooltip,
} from '@usealloy/component-library';
import { useAppSelector } from '~/store/reduxHooks';
import type { RecurringWorkflowInfo } from '~/dashboard/views/workflows/store/engine/types';
import { selectAllWorkflows, selectDiffMode } from '~/dashboard/views/workflows/store/redux/selectors';
import { WEBHOOK_KEY } from 'flow/service/actionOptions';
import { ColumnBox } from './styles';
import type { ServiceOption, ReadOnlyProp } from '../types';
import { createServiceOption } from '../serviceOptionHelpers';
import { getDiffStyleFunction, PLACEHOLDER_DIFF_STATUS } from '../../diffStyles';

export interface RecurringWebhookContainerProps extends ReadOnlyProp {
  selectedRecurringWorkflow: ServiceOption | null;
  recurringWorkflowOptions: RecurringWorkflowInfo[];
  onChange: (updatedData: ServiceOption | null) => void;
}

export default function RecurringWebhookContainer({
  selectedRecurringWorkflow,
  recurringWorkflowOptions,
  onChange,
  readOnly,
}: RecurringWebhookContainerProps) {
  const allWorkflowsByAppId = useAppSelector(selectAllWorkflows({ byApplicationId: true }));
  const isInDiffMode = useAppSelector(selectDiffMode);

  // TODO: hardcoded until the per-field diff status is wired in. The diff-mode guard is
  // redundant once that lookup returns null outside diff mode, and can be dropped then.
  const diffStatus = isInDiffMode ? PLACEHOLDER_DIFF_STATUS : null;
  const noSelectedChildOption: Option = { label: 'Select', value: null };

  const activeRecurringWorkflows = recurringWorkflowOptions.filter(option => (
    !(allWorkflowsByAppId[option.application_id]).archived));

  const buildMultiSelectWorkflowOptions = (options: RecurringWorkflowInfo[]) => (
    options.map(workflow => ({
      label: workflow.name,
      value: `${workflow.application_id}`,
    } as Option)));

  const selectionOptions = [
    noSelectedChildOption,
    ...buildMultiSelectWorkflowOptions(activeRecurringWorkflows),
  ];

  const getSelectedOption = () => {
    // Some workflows that were copied/pasted across customers incorrectly copied the configured recurring workflow for the
    // service actions, despite the new customer not having access to that recurring workflow. We wrap the following
    // in a try catch block to fail gracefully in those cases.
    try {
      return {
        label: recurringWorkflowOptions.find(
          workflow => workflow.application_id === Number(selectedRecurringWorkflow.value)).name,
        value: selectedRecurringWorkflow.value,
      } as Option;
    } catch {
      return noSelectedChildOption;
    }
  };

  const handleChange = (selectedOption: Option) => {
    if (selectedOption === noSelectedChildOption) {
      onChange(null);
      return;
    }
    if (selectedRecurringWorkflow
            && (selectedOption.value === selectedRecurringWorkflow.value)) {
      onChange(selectedRecurringWorkflow);
      return;
    }
    const serviceOption = createServiceOption({
      key: WEBHOOK_KEY,
      value: selectedOption.value,
    });
    onChange(serviceOption);
  };

  return (
    <ColumnBox>
      <Box style={{ justifyContent: 'flex-start' }}>
        <Text>Recurring Webhook Workflow</Text>
        <Tooltip
          content="Designates a recurring workflow to monitor existing entities."
        >
          <Icon icon={IconName.Question} />
        </Tooltip>
      </Box>
      <MultiSelect
        isMulti={false}
        onChange={handleChange}
        options={selectionOptions}
        selectedOptions={selectedRecurringWorkflow
          ? getSelectedOption()
          : noSelectedChildOption}
        isDisabled={readOnly}
        customStyleFunctions={getDiffStyleFunction(diffStatus)}
      />
    </ColumnBox>
  );
}
